import type { ModelGraph, ModelGraphNode, ModelNodeKind, PlanLineRow } from '../lib/api';
import { fmtCurrencyWhole } from '../lib/fmt';
import { unitSuffix, toInputValue, type DriverUnit } from '../lib/fpaDrivers';

// Layered left→right DAG: drivers on the left flow through lines/margins to
// profit & cash on the right. Columns = longest-path depth (from the server);
// edges drawn on an SVG layer, nodes as HTML on top so driver nodes can host a
// real input for interactive what-if. Controlled: the parent owns overrides +
// the scenario values to overlay.

const KIND_BG: Record<ModelNodeKind, string> = {
  driver: 'var(--color-blue-bg)', revenue: 'var(--color-green-bg)', cost: 'var(--color-amber-bg)',
  margin: 'var(--color-blue-bg)', cash: 'var(--color-green-bg)', custom: 'var(--color-surface)',
};
const KIND_BORDER: Record<ModelNodeKind, string> = {
  driver: 'var(--color-blue)', revenue: 'var(--color-green)', cost: 'var(--color-amber)',
  margin: 'var(--color-blue)', cash: 'var(--color-green)', custom: 'var(--color-border-strong)',
};

const BALANCE = new Set(['cash.closing']);

/** Node values from a set of computed plan lines (company rollup): sum over the
 *  horizon for flows, last month for balances. Used to overlay what-if results. */
export function computeNodeValues(lines: PlanLineRow[]): Map<string, number> {
  const rollup = lines.filter((l) => l.department_id === null);
  const sum = new Map<string, number>();
  const last = new Map<string, { m: string; v: number }>();
  for (const l of rollup) {
    sum.set(l.line_code, (sum.get(l.line_code) ?? 0) + l.amount);
    const c = last.get(l.line_code);
    if (!c || l.period_month > c.m) last.set(l.line_code, { m: l.period_month, v: l.amount });
  }
  const out = new Map<string, number>();
  for (const code of new Set(rollup.map((l) => l.line_code))) {
    out.set(code, BALANCE.has(code) ? (last.get(code)?.v ?? 0) : Math.round((sum.get(code) ?? 0) * 100) / 100);
  }
  return out;
}

function fmtValue(n: ModelGraphNode, value: number | null): string {
  if (value == null) return '—';
  switch (n.unit) {
    case 'ratio': return `${+(value * 100).toFixed(1)}%`;
    case 'rate': return `$${value}/hr`;
    case 'hours': return `${value}h`;
    case 'months': return `${value}mo`;
    default: return fmtCurrencyWhole(value);
  }
}

export function ModelTree({ graph, overrides, scenarioValues, editable = false, onOverrideChange }: {
  graph: ModelGraph;
  overrides?: Map<string, string>;
  scenarioValues?: Map<string, number> | null;
  editable?: boolean;
  onOverrideChange?: (code: string, value: string) => void;
}) {
  const COL_W = 212, ROW_H = 70, NODE_W = 172, NODE_H = 54, PAD = 16;
  const maxDepth = graph.nodes.reduce((m, n) => Math.max(m, n.depth), 0);
  const cols: ModelGraphNode[][] = [];
  for (let d = 0; d <= maxDepth; d++) {
    cols[d] = graph.nodes.filter((n) => n.depth === d).sort((a, b) => a.kind.localeCompare(b.kind) || a.label.localeCompare(b.label));
  }
  const pos = new Map<string, { x: number; y: number }>();
  cols.forEach((col, d) => col.forEach((n, i) => pos.set(n.code, { x: PAD + d * COL_W, y: PAD + i * ROW_H })));
  const width = PAD * 2 + (maxDepth + 1) * COL_W;
  const height = PAD * 2 + Math.max(1, ...cols.map((c) => c.length)) * ROW_H;
  const hasScenario = !!scenarioValues;

  return (
    <div style={{ overflowX: 'auto', padding: 12 }}>
      <div style={{ position: 'relative', width, height, minWidth: width }}>
        <svg width={width} height={height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
          {graph.edges.map((e, i) => {
            const a = pos.get(e.from); const b = pos.get(e.to);
            if (!a || !b) return null;
            const x1 = a.x + NODE_W, y1 = a.y + NODE_H / 2, x2 = b.x, y2 = b.y + NODE_H / 2, mx = (x1 + x2) / 2;
            return <path key={i} d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`} fill="none" stroke="var(--color-border-strong)" strokeWidth={1} opacity={0.55} />;
          })}
        </svg>
        {graph.nodes.map((n) => {
          const p = pos.get(n.code); if (!p) return null;
          const isDriver = n.kind === 'driver';
          const scen = scenarioValues?.get(n.code);
          const delta = hasScenario && !isDriver && scen != null && n.value != null ? scen - n.value : null;
          const shownVal = hasScenario && !isDriver && scen != null ? scen : n.value;
          return (
            <div key={n.code} title={`${n.code}${n.formula ? `\n= ${n.formula}` : ''}`}
              style={{ position: 'absolute', left: p.x, top: p.y, width: NODE_W, height: NODE_H, boxSizing: 'border-box',
                background: KIND_BG[n.kind], border: `0.75px solid ${KIND_BORDER[n.kind]}`, borderRadius: 6, padding: '4px 8px',
                display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.label}</div>
              {isDriver && editable ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <input
                    value={overrides?.get(n.code) ?? ''}
                    placeholder={toInputValue(n.unit as DriverUnit, n.value)}
                    onChange={(e) => onOverrideChange?.(n.code, e.target.value)}
                    type="number" step="any"
                    style={{ width: 84, fontSize: 11, padding: '1px 4px', border: '0.5px solid var(--color-border-strong)', borderRadius: 4, background: 'var(--color-surface)', color: 'var(--color-text)' }}
                  />
                  <span style={{ fontSize: 9, color: 'var(--color-text-muted)' }}>{unitSuffix(n.unit as DriverUnit)}</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{fmtValue(n, shownVal)}</span>
                  {delta != null && delta !== 0 && (
                    <span style={{ fontSize: 10, color: delta > 0 ? 'var(--color-green-text)' : 'var(--color-red-text)' }}>
                      {delta > 0 ? '+' : ''}{fmtCurrencyWhole(delta)}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
